import { clsx } from 'clsx';
import type { ReactNode } from 'react';

export type SpinnerProps = { size?: number; className?: string; label?: string };

/** Indeterminate spinner. Decorative unless `label` is given. */
export function Spinner({ size = 16, className, label }: SpinnerProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={clsx('shrink-0 animate-spin', className)}
      role={label ? 'status' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.2" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

/** Centered spinner for loading a whole page or panel. */
export function PageSpinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex min-h-48 flex-1 items-center justify-center text-slate-400">
      <Spinner size={22} label={label} />
    </div>
  );
}

export type SkeletonProps = { className?: string };

/** Loading placeholder block; size it with width/height classes. */
export function Skeleton({ className }: SkeletonProps) {
  return <div aria-hidden className={clsx('animate-pulse rounded bg-slate-200/80', className ?? 'h-4 w-full')} />;
}

export type KbdProps = { children: ReactNode; className?: string };

/** Keyboard key hint, e.g. `<Kbd>⌘K</Kbd>`. */
export function Kbd({ children, className }: KbdProps) {
  return (
    <kbd
      className={clsx(
        'inline-flex h-5 min-w-5 items-center justify-center rounded border border-slate-200 bg-slate-50 px-1 font-sans text-2xs font-medium text-slate-500',
        className,
      )}
    >
      {children}
    </kbd>
  );
}
